import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tag, Plus, Trash2, Copy, ChevronRight, ChevronLeft,
  Star, Zap, GripVertical, Pencil, Archive, Eye, Check, ExternalLink,
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-[#F5F5F5] text-[#888888]",
  active: "bg-emerald-50 text-emerald-600 border-emerald-200",
  archived: "bg-red-50 text-red-600 border-red-200",
};

export function AdminPricing() {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelDialog, setModelDialog] = useState(false);
  const [planDialog, setPlanDialog] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);

  const fetchModels = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing/models");
      setModels(data.models || []);
    } catch {
      toast.error("Failed to load promotions");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async (modelId) => {
    try {
      const { data } = await api.get(`/admin/pricing/models/${modelId}/plans`);
      setPlans(data.plans || []);
    } catch {
      toast.error("Failed to load plans");
    }
  }, []);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  useEffect(() => {
    if (selectedModel) fetchPlans(selectedModel.model_id);
    else setPlans([]);
  }, [selectedModel, fetchPlans]);

  // ── Model CRUD ──

  const handleSaveModel = async (formData) => {
    try {
      if (editingModel?.model_id) {
        await api.put(`/admin/pricing/models/${editingModel.model_id}`, formData);
        toast.success("Model updated");
      } else {
        await api.post("/admin/pricing/models", formData);
        toast.success("Model created");
      }
      setModelDialog(false);
      setEditingModel(null);
      fetchModels();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save model");
    }
  };

  const handleArchiveModel = async (modelId) => {
    try {
      await api.delete(`/admin/pricing/models/${modelId}`);
      toast.success("Model archived");
      if (selectedModel?.model_id === modelId) setSelectedModel(null);
      fetchModels();
    } catch {
      toast.error("Failed to archive model");
    }
  };

  const handleDuplicate = async (modelId) => {
    try {
      await api.post(`/admin/pricing/models/${modelId}/duplicate`);
      toast.success("Model duplicated");
      fetchModels();
    } catch {
      toast.error("Failed to duplicate");
    }
  };

  // ── Plan CRUD ──

  const handleSavePlan = async (formData) => {
    try {
      if (editingPlan?.plan_id) {
        await api.put(`/admin/pricing/plans/${editingPlan.plan_id}`, formData);
        toast.success("Plan updated");
      } else {
        await api.post(`/admin/pricing/models/${selectedModel.model_id}/plans`, formData);
        toast.success("Plan created");
      }
      setPlanDialog(false);
      setEditingPlan(null);
      fetchPlans(selectedModel.model_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save plan");
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/admin/pricing/plans/${planId}`);
      toast.success("Plan deleted");
      fetchPlans(selectedModel.model_id);
    } catch {
      toast.error("Failed to delete plan");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Detail View ──
  if (selectedModel) {
    const previewUrl = `${window.location.origin}/pricing?preview=${selectedModel.key}`;

    const copyPreviewUrl = () => {
      navigator.clipboard.writeText(previewUrl).catch(() => {
        const el = document.createElement("textarea");
        el.value = previewUrl;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      });
      toast.success("Preview URL copied");
    };

    return (
      <div className="space-y-4" data-testid="pricing-model-detail">
        {/* Back + Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedModel(null)}
            className="flex items-center gap-1 text-sm text-[#888888] hover:text-[#111111] transition-colors"
            data-testid="pricing-back-button"
          >
            <ChevronLeft size={16} /> All Models
          </button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(previewUrl, "_blank")}
              data-testid="pricing-preview-button"
            >
              <Eye size={13} className="mr-1" /> Preview
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setEditingModel(selectedModel); setModelDialog(true); }}
              data-testid="pricing-edit-model"
            >
              <Pencil size={13} className="mr-1" /> Edit Model
            </Button>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => { setEditingPlan(null); setPlanDialog(true); }}
              data-testid="pricing-add-plan"
            >
              <Plus size={14} className="mr-1" /> Add Plan
            </Button>
          </div>
        </div>

        {/* Preview URL Bar */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]">
          <Eye size={13} className="text-[#888888] shrink-0" />
          <code className="flex-1 text-[11px] font-mono text-[#888888] truncate" data-testid="pricing-preview-url">{previewUrl}</code>
          <button onClick={copyPreviewUrl} className="text-[#888888] hover:text-[#555555] transition-colors shrink-0" data-testid="pricing-copy-preview-url">
            <Copy size={13} />
          </button>
          <button onClick={() => window.open(previewUrl, "_blank")} className="text-[#888888] hover:text-[#555555] transition-colors shrink-0">
            <ExternalLink size={13} />
          </button>
        </div>

        {/* Model Info */}
        <div className="bg-[#F5F5F5] rounded-lg p-4 border border-[#E5E5E5]">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-base font-medium text-[#111111]" data-testid="pricing-model-name">{selectedModel.name}</h3>
            <Badge className={STATUS_COLORS[selectedModel.status]} variant="outline">{selectedModel.status}</Badge>
            {selectedModel.is_default && <Badge variant="secondary" className="text-[10px]">DEFAULT</Badge>}
            {selectedModel.force_active && <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[10px]" variant="outline">FORCED</Badge>}
          </div>
          <div className="flex gap-4 text-xs text-[#888888]">
            <span>Key: <code className="font-mono">{selectedModel.key}</code></span>
            <span>Priority: {selectedModel.priority}</span>
            {selectedModel.starts_at && <span>From: {selectedModel.starts_at.slice(0, 10)}</span>}
            {selectedModel.ends_at && <span>To: {selectedModel.ends_at.slice(0, 10)}</span>}
          </div>
        </div>

        {/* Plans List */}
        <div>
          <h4 className="text-sm font-medium text-[#555555] mb-3">Plans ({plans.length})</h4>
          {plans.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#888888]" data-testid="pricing-no-plans">
              No plans yet. Add your first plan.
            </div>
          ) : (
            <div className="space-y-2">
              {plans.map((plan) => (
                <div
                  key={plan.plan_id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E5] bg-white hover:border-[#E5E5E5] transition-colors"
                  data-testid={`pricing-plan-${plan.plan_id}`}
                >
                  <GripVertical size={14} className="text-[#555555] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#111111]">{plan.name}</span>
                      {plan.badge && <Badge className="text-[9px] bg-blue-50 text-blue-600 border-blue-200" variant="outline">{plan.badge}</Badge>}
                      {plan.is_highlighted && <Star size={12} className="text-amber-500 fill-amber-500" />}
                      {!plan.is_active && <Badge variant="secondary" className="text-[9px]">Inactive</Badge>}
                    </div>
                    <div className="text-xs text-[#888888] mt-0.5">
                      <code className="font-mono">{plan.key}</code> — ${plan.price} / {plan.duration_value} {plan.duration_unit}
                      {plan.compare_at_price && <span className="ml-1 line-through text-[#888888]">${plan.compare_at_price}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-[#888888] hover:text-[#555555]"
                      onClick={() => { setEditingPlan(plan); setPlanDialog(true); }}
                      data-testid={`pricing-edit-plan-${plan.plan_id}`}
                    >
                      <Pencil size={13} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-[#888888] hover:text-red-500"
                      onClick={() => handleDeletePlan(plan.plan_id)}
                      data-testid={`pricing-delete-plan-${plan.plan_id}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dialogs */}
        <ModelDialog open={modelDialog} onOpenChange={setModelDialog} model={editingModel} onSave={handleSaveModel} />
        <PlanDialog open={planDialog} onOpenChange={setPlanDialog} plan={editingPlan} onSave={handleSavePlan} />
      </div>
    );
  }

  // ── Models List View ──
  return (
    <div className="space-y-4" data-testid="pricing-models-list">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-[#111111]">Promotions</h3>
          <p className="text-xs text-[#888888] mt-0.5">{models.length} model{models.length !== 1 ? "s" : ""}</p>
        </div>
        <Button
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => { setEditingModel(null); setModelDialog(true); }}
          data-testid="pricing-create-model"
        >
          <Plus size={14} className="mr-1" /> New Model
        </Button>
      </div>

      {models.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#888888]">No promotions yet.</div>
      ) : (
        <div className="space-y-2">
          {models.map((model) => (
            <div
              key={model.model_id}
              className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E5] bg-white hover:border-[#E5E5E5] transition-colors cursor-pointer"
              onClick={() => setSelectedModel(model)}
              data-testid={`pricing-model-${model.model_id}`}
            >
              <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center shrink-0">
                <Tag size={16} className="text-[#888888]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#111111]">{model.name}</span>
                  <Badge className={`${STATUS_COLORS[model.status]} text-[10px]`} variant="outline">{model.status}</Badge>
                  {model.is_default && <Badge variant="secondary" className="text-[10px]">DEFAULT</Badge>}
                  {model.force_active && <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[10px]" variant="outline">FORCED</Badge>}
                </div>
                <div className="text-xs text-[#888888] mt-0.5">
                  <code className="font-mono">{model.key}</code>
                  {model.starts_at && <> — {model.starts_at.slice(0, 10)} to {model.ends_at?.slice(0, 10)}</>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#888888]" onClick={() => handleDuplicate(model.model_id)} data-testid={`pricing-dup-${model.model_id}`}>
                  <Copy size={13} />
                </Button>
                {model.status !== "archived" && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#888888] hover:text-red-500" onClick={() => handleArchiveModel(model.model_id)} data-testid={`pricing-archive-${model.model_id}`}>
                    <Archive size={13} />
                  </Button>
                )}
                <ChevronRight size={16} className="text-[#555555] ml-1" />
              </div>
            </div>
          ))}
        </div>
      )}

      <ModelDialog open={modelDialog} onOpenChange={setModelDialog} model={editingModel} onSave={handleSaveModel} />
    </div>
  );
}


// ─── Model Dialog ───

function ModelDialog({ open, onOpenChange, model, onSave }) {
  const [form, setForm] = useState({
    key: "", name: "", status: "draft", priority: 0,
    starts_at: "", ends_at: "", is_default: false, force_active: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (model) {
      setForm({
        key: model.key || "",
        name: model.name || "",
        status: model.status || "draft",
        priority: model.priority || 0,
        starts_at: model.starts_at?.slice(0, 16) || "",
        ends_at: model.ends_at?.slice(0, 16) || "",
        is_default: model.is_default || false,
        force_active: model.force_active || false,
      });
    } else {
      setForm({ key: "", name: "", status: "draft", priority: 0, starts_at: "", ends_at: "", is_default: false, force_active: false });
    }
  }, [model, open]);

  const handleSubmit = async () => {
    setSaving(true);
    const data = { ...form, priority: Number(form.priority) };
    if (data.starts_at) data.starts_at = new Date(data.starts_at).toISOString();
    if (data.ends_at) data.ends_at = new Date(data.ends_at).toISOString();
    await onSave(data);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pricing-model-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{model?.model_id ? "Edit" : "New"} Promotion</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Key</Label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="e.g. black_friday" className="h-9 text-sm font-mono" data-testid="model-key-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Black Friday 2026" className="h-9 text-sm" data-testid="model-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="model-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Priority</Label>
              <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="h-9 text-sm" data-testid="model-priority-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Starts at</Label>
              <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="h-9 text-sm" data-testid="model-starts-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ends at</Label>
              <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="h-9 text-sm" data-testid="model-ends-input" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="model-default-toggle" />
              <Label className="text-xs">Default</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.force_active} onCheckedChange={(v) => setForm({ ...form, force_active: v })} data-testid="model-force-toggle" />
              <Label className="text-xs">Force Active</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="model-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Plan Dialog ───

function PlanDialog({ open, onOpenChange, plan, onSave }) {
  const [form, setForm] = useState({
    key: "", name: "", duration_unit: "month", duration_value: 1,
    price: 0, currency: "usd", compare_at_price: "",
    badge: "", description: "", is_active: true, is_highlighted: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setForm({
        key: plan.key || "",
        name: plan.name || "",
        duration_unit: plan.duration_unit || "month",
        duration_value: plan.duration_value || 1,
        price: plan.price || 0,
        currency: plan.currency || "usd",
        compare_at_price: plan.compare_at_price ?? "",
        badge: plan.badge || "",
        description: plan.description || "",
        is_active: plan.is_active ?? true,
        is_highlighted: plan.is_highlighted || false,
      });
    } else {
      setForm({ key: "", name: "", duration_unit: "month", duration_value: 1, price: 0, currency: "usd", compare_at_price: "", badge: "", description: "", is_active: true, is_highlighted: false });
    }
  }, [plan, open]);

  const handleSubmit = async () => {
    setSaving(true);
    const data = {
      ...form,
      duration_value: Number(form.duration_value),
      price: Number(form.price),
      compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
      badge: form.badge || null,
      description: form.description || null,
    };
    await onSave(data);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pricing-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{plan?.plan_id ? "Edit" : "New"} Plan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Key</Label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="e.g. pro_3mo" className="h-9 text-sm font-mono" data-testid="plan-key-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Pro 3 Months" className="h-9 text-sm" data-testid="plan-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Duration</Label>
              <Input type="number" min={1} value={form.duration_value} onChange={(e) => setForm({ ...form, duration_value: e.target.value })} className="h-9 text-sm" data-testid="plan-duration-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Unit</Label>
              <Select value={form.duration_unit} onValueChange={(v) => setForm({ ...form, duration_unit: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="plan-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="year">Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="plan-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="usd">USD</SelectItem>
                  <SelectItem value="eur">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Price</Label>
              <Input type="number" step="0.01" min={0} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-9 text-sm" data-testid="plan-price-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Compare at price <span className="text-[#888888]">(optional)</span></Label>
              <Input type="number" step="0.01" value={form.compare_at_price} onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })} placeholder="e.g. 59.88" className="h-9 text-sm" data-testid="plan-compare-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Badge <span className="text-[#888888]">(optional)</span></Label>
              <Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="e.g. SAVE 17%" className="h-9 text-sm" data-testid="plan-badge-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description <span className="text-[#888888]">(optional)</span></Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Best value" className="h-9 text-sm" data-testid="plan-desc-input" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="plan-active-toggle" />
              <Label className="text-xs">Active</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_highlighted} onCheckedChange={(v) => setForm({ ...form, is_highlighted: v })} data-testid="plan-highlight-toggle" />
              <Label className="text-xs">Highlighted</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="plan-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
